import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import CustomToast from "src/Components/CustomToast";
import ReactTable from "src/Components/ReactTable/ReactTable";
import { ClientType } from "src/entity/models/FrontendActor";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { uiLoggerNamesPartnerRiskManagement } from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { DEFAULT_BRANCH_CODE, DEFAULT_COMPANY_ID } from "../../PartnerCommunication/CommonLegacy/CommonComponents";
import { NdButton } from "../../PartnerCommunication/MsmePartnerPortal/CommonComponents";
import AddFunnelReportLoading from "./AddMISReportLoading";
import AddReportConfirmationModal from "./AddReportConfirmationModal";
import AppliedFiltersSection from "./AppliedFiltersSection";
import ColumnVisibiltySection from "./ColumnVisibiltySection";
import FunnelMetricSection from "./FunnelMetricSection";
import {
  createHiddenColumnsObject,
  FUNNEL_METRIC_TYPES,
  FUNNEL_REPORT_ALL_COLUMNS,
  FUNNEL_REPORT_COLUMNS,
  MISFilters,
  REPORT_TYPES,
  statusTypes,
  STATUS_REPORT_ALL_COLUMNS,
  STATUS_REPORT_ALL_COLUMNS_SERVICE_ACCOUNTS,
  STATUS_REPORT_COLUMNS,
} from "./Interfaces";
import MISFilter from "./MISFilter";
import ReportPreviewLoading from "./ReportPreviewLoading";
import SetReportView from "./SetReportView";

export default function AddMISReport({
  allLabels,
  allCategories,
  allUsers,
  setPreviewReportDetails,
  previewReportDetails,
  listAllReports,
  funnelReportColumns,
  statusReportColumns,
}) {
  const { actor } = useContext(userContext);
  const [reportName, setReportName] = useState("");
  const [showMISFilterModal, setShowMISFilterModal] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<MISFilters>({});
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [reportData, setReportData] = useState([]);
  const [reportViews, setReportViews] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
  const [metric, setMetric] = useState({ byVolume: false, byValue: true });
  const [columnsVisibilty, setColumnsVisibilty] = useState([]);
  const [columnsOrder, setColumnsOrder] = useState<string[]>([]);
  const [isUpdatingPreview, setIsUpdatingPreview] = useState<boolean>(false);
  const [isSavingReport, setIsSavingReport] = useState<boolean>(false);
  const [tableHiddenColumns, setTableHiddenColumns] = useState({});
  const [enableUpdatePreview, setEnableUpdatePreview] = useState<boolean>(false);
  const [showAddReportConfirmationModal, setShowAddReportConfirmationModal] = useState<boolean>(false);

  const handleColumnsVisibiltyToggle = (columnKey: string, isColumnVisible: boolean) => {
    setEnableUpdatePreview(true);
    if (columnKey === statusTypes.status || columnKey === statusTypes.serviceStatus) {
      setColumnsVisibilty((prevColumns) =>
        prevColumns.map((column) => {
          const isVisible = columnKey === statusTypes.serviceStatus ? isColumnVisible : !isColumnVisible;
          if (column.key === statusTypes.status) {
            return { ...column, isVisible: isVisible };
          } else if (column.key === statusTypes.serviceStatus) {
            return { ...column, isVisible: !isVisible };
          } else {
            return column;
          }
        })
      );
    } else {
      setColumnsVisibilty((prevColumns) =>
        prevColumns.map((column) => {
          if (column.key === columnKey) {
            return { ...column, isVisible: !isColumnVisible };
          } else {
            return column;
          }
        })
      );
    }
  };

  const getReportDetails = () => {
    setIsLoadingReport(true);
    useFetch(API_ENDPOINTS.GET_REPORT_DETAILS_PARTNER_RISK_MANAGEMENT.url, "POST", {
      failureMessage: API_ENDPOINTS.GET_REPORT_DETAILS_PARTNER_RISK_MANAGEMENT.failureMessage,
      data: {
        id: previewReportDetails.id,
        reportType: previewReportDetails.reportType,
      },
      thenCallBack: (res) => {
        setReportData(res.data.reportData || []);
        setReportName(res.data.reportName);
        setReportViews(res.data.views || []);
        setAppliedFilters(res.data.filters || {});
        if (Object.keys(res.data.filters).length > 0) {
          setIsFilterApplied(true);
        }
        setColumnsOrder(res.data.columnsOrder || []);
        const hiddenColumnsObj = createHiddenColumnsObject(res.data.columnsOrder, previewReportDetails.reportType);
        setTableHiddenColumns(hiddenColumnsObj);

        setMetric({
          byVolume: res.data.metricTypes.includes(FUNNEL_METRIC_TYPES.Volume) ? true : false,
          byValue: res.data.metricTypes.includes(FUNNEL_METRIC_TYPES.Value) ? true : false,
        });
        const hiddenColumns = res.data.hiddenColumns || [];
        const statusReportAllColumnsBasedOnClientType =
          actor.clientType === ClientType.Product || actor.clientType === ClientType.ProductEnterprise
            ? STATUS_REPORT_ALL_COLUMNS
            : STATUS_REPORT_ALL_COLUMNS_SERVICE_ACCOUNTS;

        const columnVisibilityInitial = (
          previewReportDetails.reportType === REPORT_TYPES.Funnel
            ? FUNNEL_REPORT_ALL_COLUMNS
            : statusReportAllColumnsBasedOnClientType
        ).map((columnKey) => ({
          key: columnKey,
          label:
            previewReportDetails.reportType === REPORT_TYPES.Funnel
              ? FUNNEL_REPORT_COLUMNS[columnKey]
              : STATUS_REPORT_COLUMNS[columnKey],
          isVisible: !hiddenColumns.includes(columnKey), // If not in hiddenColumns, set isVisible to true
        }));

        setColumnsVisibilty(columnVisibilityInitial);
        setIsLoadingReport(false);
      },
      catchCallBack: () => {
        setIsLoadingReport(false);
      },
    });
  };

  const getReportPreview = () => {
    const metricPayload = [];
    if (metric.byVolume) {
      metricPayload.push(FUNNEL_METRIC_TYPES.Volume);
    }
    if (metric.byValue) {
      metricPayload.push(FUNNEL_METRIC_TYPES.Value);
    }
    if (metricPayload.length === 0 && previewReportDetails.reportType === REPORT_TYPES.Funnel) {
      toast.error(<CustomToast message="Please Select Atleast 1 metric" />);
      return;
    }
    setIsUpdatingPreview(true);
    useFetch(API_ENDPOINTS.GET_REPORT_PREVIEW_PARTNER_RISK_MANAGEMENT.url, "POST", {
      failureMessage: API_ENDPOINTS.GET_REPORT_PREVIEW_PARTNER_RISK_MANAGEMENT.failureMessage,
      showSuccessToast: true,
      data: {
        reportName: reportName,
        reportType: previewReportDetails.reportType === REPORT_TYPES.Funnel ? REPORT_TYPES.Funnel : REPORT_TYPES.Status,
        views: reportViews.filter((view) => view !== ""),
        filters: appliedFilters,
        hiddenColumns: columnsVisibilty.filter((column) => !column.isVisible).map((column) => column.key),
        metricTypes: metricPayload,
      },
      thenCallBack: (res) => {
        setReportData(res.data.reportData || []);
        setColumnsOrder(res.data.columnsOrder || []);
        const hiddenColumnsObj = createHiddenColumnsObject(res.data.columnsOrder, previewReportDetails.reportType);
        setTableHiddenColumns(hiddenColumnsObj);

        setTableHiddenColumns(hiddenColumnsObj);

        setIsUpdatingPreview(false);
        setEnableUpdatePreview(false);
      },
      catchCallBack: () => {
        setIsUpdatingPreview(false);
      },
    });
  };

  const handleSaveReport = () => {
    const metricPayload = [];
    if (metric.byVolume) {
      metricPayload.push(FUNNEL_METRIC_TYPES.Volume);
    }
    if (metric.byValue) {
      metricPayload.push(FUNNEL_METRIC_TYPES.Value);
    }
    if (metricPayload.length === 0 && previewReportDetails.reportType === REPORT_TYPES.Funnel) {
      toast.error(<CustomToast message="Please Select Atleast 1 metric" />);
      return;
    }

    setIsSavingReport(true);
    useFetch(API_ENDPOINTS.SAVE_MIS_REPORT_PARTNER_RISK_MANAGEMENT.url, "POST", {
      failureMessage: API_ENDPOINTS.SAVE_MIS_REPORT_PARTNER_RISK_MANAGEMENT.failureMessage,
      showSuccessToast: true,
      data: {
        id: previewReportDetails.reportAction === "editReport" ? previewReportDetails.id : null,
        reportName: reportName,
        reportType: previewReportDetails.reportType,
        views: reportViews.filter((view) => view !== ""),
        filters: appliedFilters,
        hiddenColumns: columnsVisibilty.filter((column) => !column.isVisible).map((column) => column.key),
        metricTypes: metricPayload,
      },
      thenCallBack: () => {
        setIsSavingReport(false);
        setPreviewReportDetails({ id: null, reportType: null, reportAction: null });
        listAllReports();
      },
      catchCallBack: () => {
        setIsSavingReport(false);
        setShowAddReportConfirmationModal(false);
      },
    });
  };
  useEffect(() => {
    getReportDetails();
  }, []);

  return (
    <>
      {isLoadingReport ? (
        <AddFunnelReportLoading />
      ) : (
        <>
          {/* this is top bar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: "#ffffff",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              marginTop: 1,
            }}
          >
            <TextField
              label="Report Name"
              placeholder="Report Name"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                width: "50%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />
            <Box>
              <NdButton
                disabled={isSavingReport || isUpdatingPreview}
                variant="contained"
                onClick={() => {
                  let uiLoggerFunctionName;
                  let uiLoggerMessage;
                  if (previewReportDetails.reportAction === "addReport") {
                    uiLoggerFunctionName =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_ADDNEWREPORT_ADDREPORT_CLICK.functionName;
                    uiLoggerMessage =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_ADDNEWREPORT_ADDREPORT_CLICK.message;
                  } else if (previewReportDetails.reportAction === "editReport") {
                    uiLoggerFunctionName =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_EDITREPORT_SAVEREPORT_CLICK
                        .functionName;
                    uiLoggerMessage =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_EDITREPORT_SAVEREPORT_CLICK.message;
                  } else if (previewReportDetails.reportAction === "duplicateReport") {
                    uiLoggerFunctionName =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_DUPLICATEREPORT_ADDREPORT_CLICK
                        .functionName;
                    uiLoggerMessage =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_DUPLICATEREPORT_ADDREPORT_CLICK.message;
                  }
                  uiLogger(uiLoggerFunctionName, DEFAULT_COMPANY_ID, DEFAULT_BRANCH_CODE, {
                    message: uiLoggerMessage,
                  });
                  if (enableUpdatePreview) {
                    setShowAddReportConfirmationModal(true);
                  } else {
                    handleSaveReport();
                  }
                }}
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  letterSpacing: "0.4px",
                  padding: "6px 16px",
                  marginRight: "8px",
                }}
              >
                {isSavingReport && <LoadingIcon loading={true} />}
                {previewReportDetails.reportAction === "editReport" ? "Save" : "Add"} Report
              </NdButton>
              <Button
                variant="outlined"
                onClick={() => {
                  let uiLoggerFunctionName;
                  let uiLoggerMessage;
                  if (previewReportDetails.reportAction === "addReport") {
                    uiLoggerFunctionName =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_ADDNEWREPORT_CANCEL_CLICK.functionName;
                    uiLoggerMessage = uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_ADDNEWREPORT_CANCEL_CLICK.message;
                  } else if (previewReportDetails.reportAction === "editReport") {
                    uiLoggerFunctionName =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_EDITREPORT_CANCEL_CLICK.functionName;
                    uiLoggerMessage =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_EDITREPORT_CANCEL_CLICK.message;
                  } else if (previewReportDetails.reportAction === "duplicateReport") {
                    uiLoggerFunctionName =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_DUPLICATEREPORT_CANCEL_CLICK
                        .functionName;
                    uiLoggerMessage =
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_MOREOPTIONS_DUPLICATEREPORT_CANCEL_CLICK.message;
                  }
                  uiLogger(uiLoggerFunctionName, DEFAULT_COMPANY_ID, DEFAULT_BRANCH_CODE, {
                    message: uiLoggerMessage,
                  });
                  setPreviewReportDetails({ id: null, reportType: null, reportAction: null });
                }}
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  ml: "auto",
                  padding: "6px 16px",
                  borderRadius: "4px !important",
                  color: "rgba(0, 0, 0, 0.87)",
                  borderColor: "rgba(0, 0, 0, 0.12)",
                  "&:hover, &.Mui-focusVisible": { borderColor: "rgba(0, 0, 0, 0.12)" },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
          {/* this is for 2nd row */}
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box
              sx={{
                padding: "12px 16px",
                backgroundColor: "#fafafa",
                borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                width: "75%",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  letterSpacing: "0.1px",
                  lineHeight: "157%",
                }}
              >
                Default {previewReportDetails.reportType === REPORT_TYPES.Status ? "Status" : "Funnel"} Report
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  letterSpacing: "0.4px",
                  lineHeight: "166%",
                }}
              >
                Below is the default {previewReportDetails.reportType} level report, add customization like filter and
                report view to change it as per your requirement
              </Typography>
            </Box>
            <Box
              sx={{
                padding: "12px 16px",
                backgroundColor: "#fafafa",
                borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                width: "25%",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  letterSpacing: "0.1px",
                  lineHeight: "157%",
                }}
              >
                Customise Report
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  letterSpacing: "0.4px",
                  lineHeight: "166%",
                  color: "#1976d2",
                  textDecoration: "none",
                }}
              >
                How to customise report?
              </Typography>
            </Box>
          </Box>
          {/* this for 3rd row */}
          <Box
            sx={{ display: "flex", flexDirection: "row", border: "1px solid rgba(0, 0, 0, 0.12)", background: "#fff" }}
          >
            {/* For table */}
            {
              <Box sx={{ width: "75%", padding: 1 }}>
                {isUpdatingPreview ? (
                  <ReportPreviewLoading />
                ) : (
                  <ReactTable
                    enableColumnActions={false}
                    enableGlobalFilter={false}
                    columns={
                      previewReportDetails.reportType === REPORT_TYPES.Status
                        ? statusReportColumns
                        : funnelReportColumns
                    }
                    rows={reportData}
                    enableRowSelection={false}
                    enableColumnFilters={false}
                    enableGrouping={false}
                    enableColumnPinning={false}
                    enableExpanding={true}
                    enableColumnDragging={false}
                    enableExpandAll={true}
                    enableDensityToggle={false}
                    enableColumnOrdering={false}
                    internalActionsOrder={["fullScreenToggle"]}
                    filterFromLeafRows={true}
                    initialStateColumnOrder={columnsOrder}
                    initialStateColumnVisibility={tableHiddenColumns}
                  />
                )}
              </Box>
            }
            {/* For table right content */}
            <Box
              sx={{
                width: "25%",
                background: "#fff",
                borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Box sx={{ height: "68vh", overflow: "auto" }}>
                {/* Metric section */}
                {previewReportDetails.reportType === REPORT_TYPES.Funnel && (
                  <FunnelMetricSection
                    metric={metric}
                    setMetric={setMetric}
                    setEnableUpdatePreview={setEnableUpdatePreview}
                  />
                )}
                <Divider />
                {/* Group By Section */}
                <SetReportView
                  reportType={previewReportDetails.reportType}
                  reportViews={reportViews}
                  setReportViews={setReportViews}
                  setEnableUpdatePreview={setEnableUpdatePreview}
                />
                <Divider />
                {/* Filter section */}
                <Box
                  sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <FilterListIcon color="primary" />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          letterSpacing: "0.1px",
                          lineHeight: "157%",
                          color: "#000000de",
                        }}
                      >
                        Filter Data
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontWeight: 500,
                        fontSize: "13px",
                        letterSpacing: "0.46px",
                        padding: "4px 10px",
                        borderRadius: "4px !important",
                      }}
                      onClick={() => {
                        uiLogger(
                          uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_REPORT_ADDFILTER_CLICK.functionName,
                          DEFAULT_COMPANY_ID,
                          DEFAULT_BRANCH_CODE,
                          { message: uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_REPORT_ADDFILTER_CLICK.message }
                        );
                        setShowMISFilterModal(true);
                      }}
                    >
                      + Add
                    </Button>
                  </Box>
                  {isFilterApplied && (
                    <AppliedFiltersSection
                      appliedFilters={appliedFilters}
                      setShowMISFilterModal={setShowMISFilterModal}
                      setAppliedFilters={setAppliedFilters}
                      setIsFilterApplied={setIsFilterApplied}
                      setEnableUpdatePreview={setEnableUpdatePreview}
                    />
                  )}
                </Box>
                <Divider />
                {/* Column Visibilty section */}
                <ColumnVisibiltySection
                  columnsVisibilty={columnsVisibilty}
                  handleColumnsVisibiltyToggle={handleColumnsVisibiltyToggle}
                />
              </Box>
              {/* Update report Preview Button */}
              <Box sx={{ padding: "12px 16px", background: "#FAFAFA", borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}>
                <Button
                  disabled={isUpdatingPreview || isSavingReport || !enableUpdatePreview}
                  color="primary"
                  variant="contained"
                  sx={{
                    width: "100%",
                    borderRadius: "4px !important",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "24px",
                    letterSpacing: "0.4px",
                  }}
                  onClick={() => {
                    getReportPreview();
                    uiLogger(
                      uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_REPORT_UPDATEREPORTPREVIEW_CLICK.functionName,
                      DEFAULT_COMPANY_ID,
                      DEFAULT_BRANCH_CODE,
                      {
                        message: uiLoggerNamesPartnerRiskManagement.UI_MIS_PRM_REPORT_UPDATEREPORTPREVIEW_CLICK.message,
                      }
                    );
                  }}
                >
                  {isUpdatingPreview && <LoadingIcon loading={true} />}
                  Update Report View
                </Button>
              </Box>
            </Box>
          </Box>
          {showMISFilterModal && (
            <MISFilter
              useCaseType={"reportFilter"}
              open={showMISFilterModal}
              setOpen={setShowMISFilterModal}
              allCategories={allCategories}
              allLabels={allLabels}
              allUsers={allUsers}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              setIsFilterApplied={setIsFilterApplied}
              setEnableUpdatePreview={setEnableUpdatePreview}
            />
          )}
          {showAddReportConfirmationModal && (
            <AddReportConfirmationModal
              open={showAddReportConfirmationModal}
              setOpen={setShowAddReportConfirmationModal}
              reportAction={previewReportDetails.reportAction}
              handleSaveReport={handleSaveReport}
              isSavingReport={isSavingReport}
            />
          )}
        </>
      )}
    </>
  );
}
